import * as React from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  EmptyState,
  Alert,
} from '@insolech/ui';
import { useNavigate } from 'react-router-dom';
import { Trash2, Undo2, ArrowLeft } from 'lucide-react';
import { orderQueries, orderMutations } from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { resolvePatientDisplay } from '@/features/orders/productLabel';
import { daysRemaining } from './recycleBin';

/**
 * Admin recycle bin for orders (migration 20270357).
 *
 * Reuses the page's own list query with `deletedOnly`, rather than adding a
 * parallel query: the bin and the tracker therefore share one select, one
 * set of filters and one RLS path, and cannot drift apart.
 *
 * Only admins can reach this route, and only admins can SEE these rows at
 * all: the orders SELECT policy hides anything with a non-null deleted_at
 * from every other role. The countdown is pure arithmetic on deleted_at and
 * needs no scheduled job; nothing on this page waits for one.
 */

const PAGE_SIZE = 50;

function formatWhen(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminOrdersBinPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const binQuery = useQuery({
    ...orderQueries.ordersListQuery(supabase, {
      status: 'total',
      deletedOnly: true,
      page: 0,
      pageSize: PAGE_SIZE,
      sortBy: 'created_at',
      sortDir: 'desc',
    }),
    enabled: isAdmin,
    placeholderData: keepPreviousData,
  });

  // Memoised so the deleter lookup below does not re-fire on every render:
  // `?? []` allocates a fresh array each time, which would change the memo's
  // dependency identity continuously.
  const rows = React.useMemo(() => binQuery.data?.rows ?? [], [binQuery.data]);

  // Resolve who binned each order. deleted_by is a user id; the list row does
  // not embed it, so one small lookup covers the whole page.
  const deleterIds = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.deleted_by).filter((v): v is string => !!v))),
    [rows],
  );
  const deletersQuery = useQuery({
    queryKey: ['admin', 'orders-bin', 'deleters', deleterIds],
    enabled: isAdmin && deleterIds.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', deleterIds);
      if (error) throw error;
      const out: Record<string, string> = {};
      for (const p of (data ?? []) as Array<{ id: string; full_name: string | null }>) {
        out[p.id] = p.full_name ?? p.id;
      }
      return out;
    },
  });
  const deleterName = (id: string | null): string =>
    (id ? (deletersQuery.data?.[id] ?? 'Unknown') : 'Unknown');

  const invalidate = async () => {
    setSelected(new Set());
    await qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.all });
  };

  const restoreMut = useMutation({
    mutationFn: (orderId: string) => orderMutations.restoreOrder(supabase, orderId),
    onSuccess: async () => {
      setActionError(null);
      setNotice('Order restored. It is back in the tracker with its original number.');
      await invalidate();
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : String(e)),
  });

  const purgeMut = useMutation({
    mutationFn: (orderIds: string[] | null) =>
      orderMutations.purgeDeletedOrdersNow(supabase, orderIds),
    onSuccess: async (count) => {
      setActionError(null);
      setNotice(
        count === 1
          ? '1 order destroyed for good.'
          : `${count} orders destroyed for good.`,
      );
      await invalidate();
    },
    // The referenced-order guard refuses an order still tied to a payment, a
    // Zoho invoice or a reorder. Surface its message verbatim: it names what
    // is holding the order and what to do about it.
    onError: (e) => setActionError(e instanceof Error ? e.message : String(e)),
  });

  const busy = restoreMut.isPending || purgeMut.isPending;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onEmptySelected = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (
      confirm(
        `Destroy ${ids.length} order${ids.length === 1 ? '' : 's'} for good?\n\nThis cannot be undone. Orders still linked to a payment, a Zoho invoice or a reorder will refuse and say so.`,
      )
    ) {
      purgeMut.mutate(ids);
    }
  };

  const onEmptyAll = () => {
    if (rows.length === 0) return;
    if (
      confirm(
        `Destroy every order in the recycle bin for good?\n\nThis cannot be undone. Orders still linked to a payment, a Zoho invoice or a reorder will refuse and say so.`,
      )
    ) {
      purgeMut.mutate(null);
    }
  };

  if (!isAdmin) {
    return (
      <AppShell>
        {/* Same wrapper as the orders tracker. AppShell deliberately supplies
            no gutters (its content slot is a bare overflow container), so
            every page owns its own <main>; without one this page butted the
            sidebar and ran past the viewport. */}
        <main className="px-6 py-5 max-w-[1400px] mx-auto">
          <PageHeader eyebrow="Admin" title="Recycle bin" />
          <EmptyState title="Admins only" description="The recycle bin is visible to INSOLECH admins." />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {/* EXACTLY the orders tracker's wrapper, copied from OrderTrackerPage
          rather than approximated. AppShell supplies no gutters of its own,
          so a page without this sits flush against the sidebar and runs past
          the viewport, which is what this page did. binLayout.test.ts reads
          both files and compares them, so if the tracker's wrapper ever
          changes this one fails rather than silently drifting.
          (Deliberately not restating the class string here: the test
          extracts wrappers by regex and a comment quoting it would be
          counted as a third one.) */}
      <main className="px-6 py-5 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Admin"
        title="Recycle bin"
        description="Deleted orders are kept for 60 days. Nothing is destroyed automatically."
        action={
          // An admin who arrives here needs a way out that is not the back
          // button: this page is reachable directly from the tracker header.
          <Button
            variant="outline"
            size="md"
            onClick={() => navigate('/admin/orders')}
            startIcon={<ArrowLeft className="h-4 w-4" />}
            data-testid="bin-back-to-orders"
          >
            Back to orders
          </Button>
        }
      />

      {actionError ? (
        <Alert tone="error" title="Could not complete that">
          {actionError}
        </Alert>
      ) : null}
      {notice ? (
        <Alert tone="info" title="Done">
          {notice}
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-text-secondary" data-testid="bin-count">
            {binQuery.isLoading ? 'Loading...' : `${rows.length} order${rows.length === 1 ? '' : 's'} in the bin`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || selected.size === 0}
              onClick={onEmptySelected}
              data-testid="bin-empty-selected"
            >
              <Trash2 className="h-4 w-4" /> Empty selected ({selected.size})
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || rows.length === 0}
              onClick={onEmptyAll}
              data-testid="bin-empty-all"
            >
              <Trash2 className="h-4 w-4" /> Empty all
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {binQuery.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : binQuery.isError ? (
            <Alert tone="error" title="Could not load the recycle bin">
              {binQuery.error instanceof Error ? binQuery.error.message : 'Unknown error'}
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              title="The bin is empty"
              description="Deleted orders appear here for 60 days, with who deleted them and when."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  <TableHead>Order</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Deleted by</TableHead>
                  <TableHead>Deleted</TableHead>
                  <TableHead>Days left</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const left = daysRemaining(row.deleted_at);
                  return (
                    <TableRow key={row.id} data-testid={`bin-row-${row.order_number}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selected.has(row.id)}
                          onChange={() => toggle(row.id)}
                          aria-label={`Select order ${row.order_number}`}
                          data-testid={`bin-select-${row.order_number}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-text-primary">
                        {row.order_number}
                      </TableCell>
                      <TableCell>{resolvePatientDisplay(row).name ?? '-'}</TableCell>
                      <TableCell>{deleterName(row.deleted_by)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatWhen(row.deleted_at)}</TableCell>
                      <TableCell data-testid={`bin-days-${row.order_number}`}>
                        {left === 0 ? 'past 60 days' : `${left}`}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => restoreMut.mutate(row.id)}
                          data-testid={`bin-restore-${row.order_number}`}
                        >
                          <Undo2 className="h-4 w-4" /> Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <p className="mt-3 text-[11px] text-text-muted">
            The 60 day countdown is shown from day one, but nothing is deleted automatically:
            the automatic purge has no schedule and stays switched off until it is enabled
            deliberately. Emptying the bin by hand works now.
          </p>
        </CardBody>
      </Card>
      </main>
    </AppShell>
  );
}
